import React, { useState, useEffect, useRef } from 'react';
import { NavLink } from "react-router-dom";
import { Ticket } from '../models/ticket';
import { LookupItem } from '../models/lookup-item';
import { ModalMessageType, TicketSeverity, TicketStatus, TicketType } from '../models/enums';
import { TicketDetail } from '../models/ticket-detail';
import { TicketDetailsDocument } from '../models/ticket-details-document';
import { ItemBasicInformation } from '../models/item-basic-information';
import { CrudFunctions } from '../utilities/crud-functions';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import TicketLogEntryForm from './TicketLogEntryForm';
import { PWDates } from '../utilities/pw-dates';
import { DocumentationConstants } from '../store/documentation-constants';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import DmaFooter from './DmaFooter';

import DataGrid, {
    Column,
    FilterRow,
    Pager,
    Paging,
    FilterPanel,
    FilterBuilderPopup,
    Position,
    Selection,
    HeaderFilter,
    Editing,
    Popup as DataGridPopup,
    Form,
    Lookup,
    Item as ToolbarItem,
    Toolbar,
    SearchPanel,
    Button as ColumnButton,
    LoadPanel
} from 'devextreme-react/data-grid';
import type { DataGridRef } from 'devextreme-react/data-grid';

import { SelectBox } from 'devextreme-react';
import { Popup as DialogPopup } from 'devextreme-react/popup';
import { Item } from 'devextreme-react/form';
import HtmlEditor from 'devextreme-react/html-editor';
import { Button } from 'devextreme-react/button';
import { ValidationRule } from 'devextreme/common';

/*
const filterBuilderPopupPosition = {
    of: window,
    at: 'top',
    my: 'top',
    offset: { y: 10 }
};
*/

const filters = [
    {
        name: "All Open",
        filter: [['status', '<>', 0]],
    }, {
        name: "Priority Set",
        filter: [['status', '<>', 0], ['priority', '>', 0]],
    }, {
        name: "Severity High",
        filter: [['status', '<>', 0], ['severity', '=', 4]]
    }, {
        name: "Pilot Review",
        filter: [['status', '=', 0], ['severity', '=', 2]]
    }, {
        name: "All (Including closed)",
        filter: [],
    },

];

const ticketMessageValidationRules: ValidationRule[] = [
    { type: 'required', message: 'Message is required.' },
    { type: 'stringLength', min: 3, max: 100, message: 'Name must be between 3 and 60 characters in length.' }
];

const ticketDescriptionValidationRules: ValidationRule[] = [
    { type: 'stringLength', min: 0, max: 500, message: 'Description must be shorter than 500 characters in length.' }
];

const ticketTicketTypeValidationRules: ValidationRule[] = [
    { type: 'required', message: 'Ticket Type is required.' },
];

const ticketSeverityValidationRules: ValidationRule[] = [
    { type: 'required', message: 'Severity is required.' },
];

const ticketStatusValidationRules: ValidationRule[] = [
    { type: 'required', message: 'Status is required.' },
];

let key = 1;

export default function TicketsForm() {
    const dataGridRef = useRef<DataGridRef>(null);

    const [tickets, setTickets] =
        useState<Ticket[]>();

    const [selectedTicket, setSelectedTicket] =
        useState<Ticket>();

    const [selectedTicketDetail, setSelectedTicketDetail] =
        useState<TicketDetailsDocument>();

    const [ticketTypes, setTicketTypes] =
        useState<LookupItem[]>();

    const [ticketSeverities, setTicketSeverities] =
        useState<LookupItem[]>();

    const [ticketStatuses, setTicketStatuses] =
        useState<LookupItem[]>();

    const [selectedTicketContent, setSelectedTicketContent] =
        useState<string>();

    const [filterName, setFilterName] =
        useState<string>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [ticketLogEntryDialogVisible, setTicketLogEntryDialogVisible] =
        useState<boolean>();

    const [ticketLogDetailOnly, setTicketLogDetailOnly] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    useEffect(() => {
        getAllData();
    }, []);

    useEffect(() => {
        changeTicketsFilter();
    }, [filterName]);

    key++;

    return (
        <div className="pw-content">
            <div id="pb-title-pane">
                <div className="lq-title-bar">
                    <div className="lq-main-title">Ticket Log</div>
                    <div className="title-bar-spacer" />
                    <div className="save-toolbar">

                        <div>
                            <Button className="title-bar-button"
                                text="" icon="arrowleft" type="normal"
                                hint="go back"
                                onClick={goBack} />
                        </div>
                        <div>
                            <NavLink to={`/documentation/${DocumentationConstants.TicketLogForm}`}
                                className="pw-right-align-content" target="_blank" rel="noopener noreferrer">
                                <Button className="title-bar-button pw-orange"
                                    text="" icon="help" type="normal"
                                    hint="help and documentation" />
                            </NavLink>
                        </div>
                    </div>
                </div>
            </div>

            <div id="roles-datagrid-container">
                <DataGrid keyExpr="ticketId"
                    ref={dataGridRef}
                    height='26rem' width="100%"
                    dataSource={tickets ? tickets : []}
                    onSelectionChanged={ticketsSelectionChanged}
                    onRowValidating={ticketValidating}
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    showBorders={true}
                    repaintChangesOnly={true} >

                    <LoadPanel enabled={true} />
                    <Selection mode={'single'} />
                    <HeaderFilter visible={true} />
                    <FilterRow visible={true} />
                    <FilterPanel visible={true} />
                    <FilterBuilderPopup>
                        <Position of={window} at='center' my='center' />
                    </FilterBuilderPopup>
                    <SearchPanel visible={true}
                        width={240}
                        placeholder="Search..." />
                    <Paging defaultPageSize={30} />
                    <Pager
                        showPageSizeSelector={true}
                        allowedPageSizes={[30, 100, 200]}
                        showInfo={true} />

                    <Column type="buttons" width='6rem'>
                        <ColumnButton name="edit" />
                        <ColumnButton icon="hidepanel" hint="close issue" onClick={onCloseTicket} />
                        <ColumnButton hint="information" icon="info"
                            onClick={onTicketsInformationButtonClicked} />
                    </Column>

                    <Column dataField="ticketNumber" caption="Ticket#" width='6rem' allowEditing={false} dataType="string" />
                    <Column caption="" width='1.6rem' allowFiltering={false}
                        cellRender={ticketStatusIconRender} />

                    <Column dataField="priority" width="6rem" dataType="number"
                        defaultSortOrder="desc" />

                    {/*
                    <Column dataField="priority" width="6rem" dataType="number"
                       defaultSortOrder="desc" filterValue={1}>
                        <HeaderFilter dataSource={ticketsPriorityFilter} /> 
                    </Column>
                    */}

                    <Column dataField="status" width='8rem' dataType="number"
                        cellRender={ticketStatusRender}>
                        <Lookup dataSource={ticketStatuses ? ticketStatuses : []}
                            displayExpr={'name'} valueExpr={'id'} />
                    </Column>
                    <Column dataField="ticketType" width='6rem' caption="Type"
                        cellRender={ticketTypeRender} dataType="number"
                        validationRules={ticketTicketTypeValidationRules} >
                        <Lookup dataSource={ticketTypes ? ticketTypes : []}
                            displayExpr={'name'} valueExpr={'id'} />
                    </Column>

                    <Column dataField="severity" width='7rem' dataType="number"
                        cellRender={ticketSeverityRender}
                        validationRules={ticketSeverityValidationRules} >
                        <Lookup dataSource={ticketSeverities ? ticketSeverities : []}
                            displayExpr={'name'} valueExpr={'id'} />
                    </Column>

                    <Column dataField="description" dataType="string"
                        validationRules={ticketDescriptionValidationRules} />

                    <Column dataField="createUsername" caption="Created By" width='7rem' allowEditing={false} dataType="string" />
                    <Column dataField="createTimestamp" dataType="date" allowEditing={false}
                        caption="Date" width='9rem' format="MM/dd/yyyy HH:mm:ss" />

                    <Toolbar>
                        <ToolbarItem location='after'>
                            <Button icon='add'
                                hint='Add new ticket'
                                onClick={showTicketLogForAddingTicket} />
                        </ToolbarItem>
                        <ToolbarItem location='after'>
                            <Button icon='refresh'
                                hint='Refresh data'
                                onClick={getTickets} />
                        </ToolbarItem>

                        <ToolbarItem location='after'>
                            <SelectBox items={filters} width="15rem"
                                valueExpr="name" displayExpr="name"
                                value={filterName}
                                onValueChanged={onChangeTicketsFilterName} />
                        </ToolbarItem>
                        <ToolbarItem name="searchPanel" location='after' />
                    </Toolbar>

                    <Editing
                        mode="popup"
                        useIcons={true}
                        allowAdding={false}
                        allowDeleting={false}
                        allowUpdating={true}>

                        <DataGridPopup title="Edit Ticket" showTitle={true} width="80vw" height="80vh" />

                        <Form colCount={2}>
                            <Item dataField="ticketType"
                                validationRules={ticketTicketTypeValidationRules} />
                            <Item dataField="severity"
                                validationRules={ticketSeverityValidationRules} />
                            <Item dataField="priority" />
                            <Item dataField="status"
                                validationRules={ticketStatusValidationRules} />
                            <Item dataField="description" colSpan={2} colCount={2}
                                validationRules={ticketDescriptionValidationRules} />
                        </Form>
                    </Editing>
                </DataGrid>
            </div>

            {selectedTicket &&
                <div className="pw-margin-top-1">
                    <div className="lq-sub-title pw-margin-top-05 pw-margin-bottom-05">{`Ticket #${selectedTicket.ticketNumber} Details`}</div>
                    <div>
                        <HtmlEditor height='100%' readOnly={true}
                            value={selectedTicketContent ? selectedTicketContent : ''}>
                        </HtmlEditor>
                    </div>

                    <div className="pw-comp-row pw-margin-top-05">
                        <Button height={'1.6rem'} disabled={!selectedTicket}
                            type="success" text="Add Ticket Detail" onClick={showTicketLogForAddingDetail} />
                    </div>
                </div>
            }

            <DmaFooter />

            {ticketLogEntryDialogVisible &&
                <DialogPopup visible={ticketLogEntryDialogVisible}
                    onHiding={hideTicketLogEntryDialog}
                    title="New Ticket Detail"
                    width='80vw'
                    height='80vh'
                    dragEnabled={true}
                    resizeEnabled={true}>
                    <TicketLogEntryForm detailOnly={ticketLogDetailOnly}
                        onCancel={hideTicketLogEntryDialog}
                        onApplyDetail={addDetailToTicket}
                        onComplete={onTicketLogEntryComplete}
                        key={`TLE${key}`} />
                </DialogPopup>
            }

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width='25rem'
                    height='21rem'
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }
        </div>
    );

    function onChangeTicketsFilterName(e: any) {
        setFilterName(e.value);
    }

    function changeTicketsFilter() {
        if (filterName) {
            const dataGrid = dataGridRef.current?.instance();

            if (filterName === 'All (Including closed)') {
                dataGrid?.clearFilter();
            } else {
                let matchingFilter = filters.filter(f => f.name === filterName);

                //console.log("matchingFilter:");
                //console.log(matchingFilter);

                if (matchingFilter && matchingFilter[0].filter)
                    dataGrid?.filter(matchingFilter[0].filter);
            }
        }
    }

    function onTicketsInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            let itemInformation: ItemBasicInformation = {
                rowPointer: e.row.data.ticketId,
                createTimestamp: e.row.data.createTimestamp,
                lastModifiedTimestamp: e.row.data.modifyTimestamp,
                createUser: e.row.data.createUser,
                modifyUser: e.row.data.modifyUser
            };

            setItemBasicInformation(itemInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    async function ticketsSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0) {
            let ticket = e.selectedRowsData[0] as Ticket;

            setSelectedTicket(ticket);

            if (ticket.ticketId) {
                getTicketDetails(ticket.ticketId);
            }
        }
    }

    function constructTicketDetailHeader(ticketDetail: TicketDetail) {
        let detailDate = PWDates.getDateTimeString(ticketDetail.createTimestamp);

        return `<br /><p><p><strong style="margin-top:1rem;">Created by: ${ticketDetail.createUsername}</strong></p>
            <p><span style="font-size:0.8em; color: rgb(4,12,116);">${detailDate}</span></p></p><br />`;
    }

    function concatenateTicketDetails(ticketDetailsDocument: TicketDetailsDocument) {
        if (ticketDetailsDocument.ticketDetails.length > 0) {
            let selectedTicketContent = '';

            ticketDetailsDocument.ticketDetails.forEach(td => {
                selectedTicketContent += (constructTicketDetailHeader(td) + (td.content ? td.content : ''));
            });

            //this.setState({ selectedTicketContent: selectedTicketContent });
            setSelectedTicketContent(selectedTicketContent);
        }
    }

    /*
    //todo: need to requery details only
    //TODO: make sure this works!
    //changed on 4/11/2024 - look at previous if i hosed this
    function addTicketDetailToCurrentDetails(ticketDetail: TicketDetail) {
        if (selectedTicketDetail && selectedTicketDetail.ticketDetails) {

            let editedSelectedTicketDetails = [...selectedTicketDetail.ticketDetails];

            //let ticketDetails = selectedTicketDetail.ticketDetails;

            editedSelectedTicketDetails.push(ticketDetail);

            //this.setState({ selectedTicketDetail: { ...prevSelectedTicketDetail, ticketDetails: ticketDetails } });

            setSelectedTicketDetail({ ...selectedTicketDetail, ticketDetails: editedSelectedTicketDetails });

            concatenateTicketDetails(selectedTicketDetail);
        }
    }
    */

    async function ticketValidating(e: any) {
        if (e.brokenRules && e.brokenRules.length > 0)
            return;

        if (e.oldData && e.newData) {
            let newData = e.newData;
            let updatedTicket = { ...e.oldData, ...newData } as Ticket;

            await updateTicket(updatedTicket)
                .catch((res) => {
                    let mm: ModalMetadata = {
                        error: res,
                        mode: ModalMessageType.Error,
                        title: "Database Update Error"
                    };

                    setGlobalDialogMetadata(mm);
                    setGlobalDialogVisible(true);
                });
        }
    }

    async function addDetailToTicket(detail: TicketDetail) {
        if (selectedTicket && selectedTicket.ticketId) {

            const ticketId = selectedTicket.ticketId;
            detail.ticketId = ticketId;

            await insertTicketDetail(detail)
                .then((data) => {
                    hideTicketLogEntryDialog();
                    getTicketDetails(ticketId);
                })
                .catch((res) => {
                    let mm: ModalMetadata = {
                        error: res,
                        mode: ModalMessageType.Error,
                        title: "Database Update Error"
                    };

                    setGlobalDialogMetadata(mm);
                    setGlobalDialogVisible(true);
                });
        }
    }

    function onTicketLogEntryComplete() {
        hideTicketLogEntryDialog();
        getTickets();
    }

    async function onCloseTicket(e: any) {
        if (e.row && e.row.data) {
            let deleteTicket = e.row.data as Ticket;

            if (deleteTicket && deleteTicket.ticketId) {
                await closeTicket(deleteTicket.ticketId)
                    .then(() => {
                        getTickets();
                    })
                    .catch((res) => {
                        let mm: ModalMetadata = {
                            error: res,
                            mode: ModalMessageType.Error,
                            title: "Error while closing the ticket"
                        };

                        setGlobalDialogMetadata(mm);
                        setGlobalDialogVisible(true);
                    });
            }
        }
    }

    async function getTickets() {
        await CrudFunctions.getData("support/tickets")
            .then((data) => {
                //console.log("tickets:");
                //console.log(data);

                setTickets(data);
            })
            .catch((res) => {
                let mm: ModalMetadata = {
                    error: res,
                    mode: ModalMessageType.Error,
                    title: "Error retrieving Tickets"
                };

                setGlobalDialogMetadata(mm);
                setGlobalDialogVisible(true);
            });
    }

    async function getTicketDetails(ticketDetailId: string) {
        await CrudFunctions.getData(`support/ticketdetails/${ticketDetailId}`)
            .then((res) => {
                let ticketDetailsDocument = res as TicketDetailsDocument;

                if (ticketDetailsDocument) {
                    concatenateTicketDetails(ticketDetailsDocument);
                    setSelectedTicketDetail(ticketDetailsDocument);
                }
            })
            .catch((res) => {
                let mm: ModalMetadata = {
                    error: res,
                    mode: ModalMessageType.Error,
                    title: "Error retrieving Ticket Details"
                };

                setGlobalDialogMetadata(mm);
                setGlobalDialogVisible(true);
            });
    }

    //TODO: call all thses at once in a single object:

    async function getTicketTypes() {
        await CrudFunctions.getData("support/tickettypes")
            .then((data) => {
                setTicketTypes(data);
            })
            .catch((res) => {
                let mm: ModalMetadata = {
                    error: res,
                    mode: ModalMessageType.Error,
                    title: "Error retrieving Ticket Types"
                };

                setGlobalDialogMetadata(mm);
                setGlobalDialogVisible(true);
            });
    }

    async function getTicketSeverities() {
        await CrudFunctions.getData("support/ticketseverities")
            .then((data) => {
                setTicketSeverities(data);
            })
            .catch((res) => {
                let mm: ModalMetadata = {
                    error: res,
                    mode: ModalMessageType.Error,
                    title: "Error retrieving Ticket Severities"
                };

                setGlobalDialogMetadata(mm);
                setGlobalDialogVisible(true);
            });
    }

    async function getTicketStatuses() {
        await CrudFunctions.getData("support/ticketstatuses")
            .then((data) => {
                setTicketStatuses(data);
            })
            .catch((res) => {
                let mm: ModalMetadata = {
                    error: res,
                    mode: ModalMessageType.Error,
                    title: "Error retrieving Ticket Statuses"
                };

                setGlobalDialogMetadata(mm);
                setGlobalDialogVisible(true);
            });
    }

    async function insertTicketDetail(ticketDetail: TicketDetail) {
        return await CrudFunctions.postData("support/ticketdetail", ticketDetail);
    }

    async function updateTicket(ticket: Ticket) {
        return await CrudFunctions.putData("support/ticket", ticket);
    }

    async function closeTicket(ticketId: string) {
        return await CrudFunctions.deleteData(`support/closeticket/${ticketId}`);
    }

    //navigation

    function goBack() {
        history.back();
    }

    //show, hide

    function showTicketLogForAddingDetail() {
        showTicketLogEntryDialog(true);
    }

    function showTicketLogForAddingTicket() {
        showTicketLogEntryDialog(false);
    }

    function showTicketLogEntryDialog(detailOnly: boolean) {
        setTicketLogEntryDialogVisible(true);
        setTicketLogDetailOnly(detailOnly);
    }

    function hideTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(false);
    }

    function showItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(true);
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    //render

    //TODO : move these to a common location:

    function ticketTypeRender(e: any) {
        if (e.data && e.data.ticketType) {
            let colorCss = "pw-blue";

            switch (e.data.tickeType) {
                case TicketType.BugReport:
                    colorCss = "pw-red";
                    break;
                case TicketType.Documentation:
                    colorCss = "pw-green";
                    break;
                case TicketType.Maintenance:
                    colorCss = "pw-green";
                    break;
                case TicketType.DataUpdate:
                    colorCss = "pw-red";
                    break;
            }

            return (
                <div>
                    <div className={colorCss}>{e.displayValue}</div>
                </div>
            );
        }
        else
            return (<div>{e.displayValue}</div>);
    }

    function ticketSeverityRender(e: any) {
        if (e.data && e.data.severity) {
            let colorCss = "";

            switch (e.data.severity) {
                case TicketSeverity.Immediate:
                    colorCss = "pw-red";
                    break;
                case TicketSeverity.High:
                    colorCss = "pw-pink";
                    break;
                case TicketSeverity.Medium:
                    colorCss = "pw-purple";
                    break;
                case TicketSeverity.Low:
                    colorCss = "pw-orange";
                    break;
                default:
                    colorCss = "pw-black";
                    break;
            }

            return (
                <div>
                    <div className={colorCss}>{e.displayValue}</div>
                </div>
            );
        }
        else
            return (<div>{e.displayValue}</div>);
    }

    function ticketStatusIconRender(e: any) {
        if (e.data) {
            switch (e.data.status) {
                case TicketStatus.InProcess:
                    return <span className="material-symbols-outlined tab-icon pw-blue">directions_run</span>;
                case TicketStatus.Closed:
                    return <span className="material-symbols-outlined tab-icon pw-gray-600">cancel</span>;
                case TicketStatus.PilotReview:
                    return <span className="material-symbols-outlined tab-icon pw-orange">visibility</span>;
                case TicketStatus.Submitted:
                    return <span className="material-symbols-outlined tab-icon pw-azure">send</span>;
                case TicketStatus.ReadyForProduction:
                    return <span className="material-symbols-outlined tab-icon pw-green">check</span>;
                case TicketStatus.UpdateError:
                    return <span className="material-symbols-outlined tab-icon pw-red">troubleshoot</span>;
                case TicketStatus.Reviewing:
                    return <span className="material-symbols-outlined tab-icon pw-orange">psychology_alt</span>;
                case TicketStatus.Approved:
                    return <span className="material-symbols-outlined tab-icon pw-green">add_task</span>;
                case TicketStatus.Postponed:
                    return <span className="material-symbols-outlined tab-icon pw-gray-600">archive</span>;

                default:
                    return <div></div>;
            }
        }
        else
            return <div></div>;
    }

    function ticketStatusRender(e: any) {
        if (e.data) {
            let colorCss = "";

            switch (e.data.status) {
                case TicketStatus.InProcess:
                    colorCss = "pw-blue";
                    break;
                case TicketStatus.Closed:
                    colorCss = "pw-gray-600";
                    break;
                case TicketStatus.PilotReview:
                    colorCss = "pw-orange";
                    break;
                case TicketStatus.Submitted:
                    colorCss = "pw-azure";
                    break;
                case TicketStatus.ReadyForProduction:
                    colorCss = "pw-green";
                    break;
                case TicketStatus.UpdateError:
                    colorCss = "pw-red";
                    break;
                case TicketStatus.Reviewing:
                    colorCss = "pw-orange";
                    break;
                case TicketStatus.Approved:
                    colorCss = "pw-green";
                    break;
                case TicketStatus.Postponed:
                    colorCss = "pw-gray-600";
                    break;
                default:
                    colorCss = "pw-black";
                    break;
            }

            return (
                <div>
                    <div className={colorCss}>{e.displayValue}</div>
                </div>
            );
        }
        else
            return <div>NA</div>;
    }

    async function getAllData() {
        await Promise.all([
            getTickets(),
            getTicketTypes(),
            getTicketSeverities(),
            getTicketStatuses()
        ]);
    }
}
